import { Row, SortingFn } from '@tanstack/react-table'
import { DataTableV2Column } from './types'

const normalizeStringSortValue = (value: unknown): string => {
  if (typeof value === 'string') return value.trim()
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)

  // Handle title/value object fields returned by some backends.
  if (typeof value === 'object' && value !== null) {
    const obj = value as { title?: unknown; value?: unknown }
    if (typeof obj.title === 'string') return obj.title.trim()
    if (typeof obj.value === 'string') return obj.value.trim()
    if (typeof obj.value === 'number' || typeof obj.value === 'boolean') return String(obj.value)
  }

  return ''
}

/**
 * Sorting function for numeric columns
 */
export const createNumberSortFn = <T>(columnId: string): SortingFn<T> => {
  return (rowA: Row<T>, rowB: Row<T>) => {
    const valueA = ((rowA.original as Record<string, unknown>)[columnId] as number) || 0
    const valueB = ((rowB.original as Record<string, unknown>)[columnId] as number) || 0
    return valueA - valueB
  }
}

/**
 * Sorting function for date/datetime columns
 */
export const createDateSortFn = <T>(columnId: string): SortingFn<T> => {
  return (rowA: Row<T>, rowB: Row<T>) => {
    const dateA = new Date((rowA.original as Record<string, unknown>)[columnId] as string)
    const dateB = new Date((rowB.original as Record<string, unknown>)[columnId] as string)
    return dateA.getTime() - dateB.getTime()
  }
}

/**
 * Sorting function for string columns (default)
 */
export const createStringSortFn = <T>(columnId: string): SortingFn<T> => {
  return (rowA: Row<T>, rowB: Row<T>) => {
    const valueA = normalizeStringSortValue((rowA.original as Record<string, unknown>)[columnId])
    const valueB = normalizeStringSortValue((rowB.original as Record<string, unknown>)[columnId])
    return valueA.localeCompare(valueB)
  }
}

/**
 * Sorting function for boolean columns
 */
export const createBooleanSortFn = <T>(columnId: string): SortingFn<T> => {
  return (rowA: Row<T>, rowB: Row<T>) => {
    const valueA = ((rowA.original as Record<string, unknown>)[columnId] as boolean) ? 1 : 0
    const valueB = ((rowB.original as Record<string, unknown>)[columnId] as boolean) ? 1 : 0
    return valueA - valueB
  }
}

/**
 * Gets the appropriate sorting function based on V2 column data display configuration
 */
export const getSortingFnV2 = <T>(
  columnId: string,
  dataDisplay: DataTableV2Column['dataDisplay']
): SortingFn<T> => {
  // Check for type-based data display
  if ('type' in dataDisplay) {
    switch (dataDisplay.type) {
      case 'number':
        return createNumberSortFn<T>(columnId)
      case 'date-string':
        return createDateSortFn<T>(columnId)
      case 'boolean':
        return createBooleanSortFn<T>(columnId)
      case 'string':
      default:
        return createStringSortFn<T>(columnId)
    }
  }

  // Check for component-based data display
  if ('component' in dataDisplay) {
    // Badge and ProgressStatus are typically string-based
    return createStringSortFn<T>(columnId)
  }

  // Default to string sorting
  return createStringSortFn<T>(columnId)
}
